import { useTranslation } from 'react-i18next'

import { Button } from '@ui/components/Button'

import { useWindowResize } from '@utils/domUtils'

import type { DxTradeSide, FixOrderGroupPayload, FixSingleOrderPayload } from '@model/DxTrade.types'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import { formatValueWithStep, getStepDecimals } from '../utils/helpers'

interface OrderWidgetBottomProps {
  side: DxTradeSide
  currentPrice?: number
  orderPayload?: FixSingleOrderPayload | FixOrderGroupPayload | null
  priceStep?: number
  disabled?: boolean
  onSubmit?: () => void
  isSubmitting?: boolean
}

export const OrderWidgetBottom: React.FC<OrderWidgetBottomProps> = ({
  side,
  currentPrice,
  orderPayload,
  priceStep,
  disabled,
  onSubmit,
  isSubmitting,
}) => {
  const { t } = useTranslation()
  const { formatNumber } = useFormatNumber()
  const fractionDigits = getStepDecimals(priceStep)
  const isMobile = useWindowResize()
  if (!side) {
    return null
  }
  const isBuy = side === 'BUY'

  const mainOrder = orderPayload && 'orders' in orderPayload ? orderPayload.orders[0] : orderPayload

  const isLimitOrStop = mainOrder?.ordType === '2' || mainOrder?.ordType === '3'

  const rawPrice = isLimitOrStop ? mainOrder?.price : currentPrice

  const displayPrice = (() => {
    if (rawPrice == null) return ''
    if (isLimitOrStop) {
      return formatValueWithStep(rawPrice, priceStep)
    }
    return formatNumber(rawPrice, {
      minDecimalPlaces: fractionDigits,
      maxDecimalPlaces: fractionDigits,
    })
  })()

  const spacingProps = isMobile
    ? { pt: 'x8' as const }
    : ({ pt: 'x16', pb: 'x16', px: 'x16' } as const)

  return (
    <Box flex align='center' justify='center' {...spacingProps}>
      <Button
        size='lg'
        variant={isBuy ? 'success' : 'danger'}
        fullWidth
        tabularNums
        disabled={disabled || !onSubmit}
        loading={isSubmitting}
        onClick={onSubmit}
      >
        {`${isBuy ? t('OrderWidget.Buy') : t('OrderWidget.Sell')} ${displayPrice}`}
      </Button>
    </Box>
  )
}
